import React, {
	ButtonHTMLAttributes,
	ReactElement,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { CCButton } from "@pymblecitizencoders/cc-ui";

/**
 * cc-ui's CCMenu, but with the popup rendered into `document.body`.
 *
 * ## Why this exists rather than CCMenu
 *
 * CCMenu puts its popup inside the trigger's own `.cc-menu` wrapper, absolutely
 * positioned. That is right almost everywhere and wrong inside a scroller: the
 * attendee roll lives in `.em-att-table-wrap`, which is `overflow: auto` so the
 * table can scroll under a sticky header, and an overflow box clips its
 * descendants. Opening the approval menu on the last visible row drew it cut off
 * at the table's edge, so the options could not be read or clicked.
 *
 * There is no CCMenu prop for this. Only CCModal and the toast provider portal
 * in the kit, so the fix lives here until the kit grows one.
 *
 * ## Positioning
 *
 * Fixed coordinates measured off the trigger, not tracked. `position: fixed`
 * escapes every ancestor's overflow, and the popup is aligned by EDGE rather
 * than by a computed width: `right` against the viewport for align="end",
 * `left` for "start", so nothing has to be measured before it renders.
 *
 * NOT TRACKED, so any scroll closes it. The alternative is following the
 * trigger on every scroll and resize, which for a two-item menu is a lot of
 * machinery to keep a popup glued to a row the reader has already left. The
 * kit's own menus behave the same way when the page moves under them.
 *
 * The styling is the kit's: same `cc-menu__popup` class, same `data-surface`,
 * same CCButton rows, so this reads as a cc-ui menu because it is one, minus
 * the containing block.
 */

export interface PortalMenuItem {
	id: string;
	label: string;
	onSelect: () => void;
	selected?: boolean;
	disabled?: boolean;
}

interface PortalMenuProps {
	/** A real button, as CCMenu takes, so the trigger keeps its own treatment. */
	trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
	items: PortalMenuItem[];
	/** Which edge of the popup lines up with the trigger's. */
	align?: "start" | "end";
	className?: string;
}

/** Space between the trigger and the popup, matching the kit's `--app-space-xs`. */
const GAP = 4;

/** Roughly one row, for deciding whether the menu fits below the trigger. */
const ROW = 36;

export const PortalMenu: React.FC<PortalMenuProps> = ({
	trigger,
	items,
	align = "end",
	className,
}) => {
	const id = useId();
	const [open, setOpen] = useState(false);
	const [box, setBox] = useState<React.CSSProperties | null>(null);
	const anchorRef = useRef<HTMLSpanElement>(null);
	const popupRef = useRef<HTMLDivElement>(null);
	const firstItemRef = useRef<HTMLButtonElement>(null);

	const place = useCallback(() => {
		const el = anchorRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		/* Estimated rather than measured: the popup does not exist yet, and one
		   row per item plus its padding is close enough to decide which side of
		   the trigger it goes on. Being a few pixels out only matters when it is
		   within a few pixels of the viewport edge, where either choice is
		   equally cramped. */
		const height = items.length * ROW + 16;
		const below = window.innerHeight - rect.bottom;
		setBox({
			position: "fixed",
			...(below >= height + GAP
				? { top: rect.bottom + GAP }
				: { bottom: window.innerHeight - rect.top + GAP }),
			...(align === "end"
				? { right: window.innerWidth - rect.right }
				: { left: rect.left }),
		});
	}, [align, items.length]);

	useEffect(() => {
		if (!open) return;
		firstItemRef.current?.focus();

		const closeFromOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			if (anchorRef.current?.contains(target)) return;
			if (popupRef.current?.contains(target)) return;
			setOpen(false);
		};
		const closeFromEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};
		const closeFromMovement = () => setOpen(false);

		document.addEventListener("mousedown", closeFromOutside);
		document.addEventListener("keydown", closeFromEscape);
		/* Capture, because the scroll that matters is the TABLE's and a scroll
		   event does not bubble to the window from the element that scrolled. */
		window.addEventListener("scroll", closeFromMovement, true);
		window.addEventListener("resize", closeFromMovement);
		return () => {
			document.removeEventListener("mousedown", closeFromOutside);
			document.removeEventListener("keydown", closeFromEscape);
			window.removeEventListener("scroll", closeFromMovement, true);
			window.removeEventListener("resize", closeFromMovement);
		};
	}, [open]);

	const suppliedClick = trigger.props.onClick;
	const decorated = React.cloneElement(trigger, {
		"aria-haspopup": "menu",
		"aria-expanded": open,
		"aria-controls": open ? id : undefined,
		onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
			suppliedClick?.(event);
			if (event.defaultPrevented) return;
			if (!open) place();
			setOpen((wasOpen) => !wasOpen);
		},
	} as Partial<ButtonHTMLAttributes<HTMLButtonElement>>);

	const isChoiceMenu = items.some((item) => item.selected !== undefined);

	return (
		<span
			ref={anchorRef}
			className={className ? `cc-menu ${className}` : "cc-menu"}
		>
			{decorated}
			{open &&
				box &&
				createPortal(
					<div
						ref={popupRef}
						id={id}
						className={`cc-menu__popup cc-menu__popup--${align}`}
						role="menu"
						data-surface="elevated"
						/* Inline, so it beats the kit's `position: absolute` and its
						   `top: calc(100% + …)`, which mean nothing to a portalled
						   node whose parent is the body. */
						style={box}
					>
						{items.map((item, index) => (
							<CCButton
								key={item.id}
								ref={index === 0 ? firstItemRef : undefined}
								variant="clear"
								fullWidth
								role={isChoiceMenu ? "menuitemradio" : "menuitem"}
								aria-checked={isChoiceMenu ? Boolean(item.selected) : undefined}
								selected={isChoiceMenu ? Boolean(item.selected) : undefined}
								disabled={item.disabled}
								onClick={() => {
									item.onSelect();
									setOpen(false);
								}}
							>
								{item.label}
							</CCButton>
						))}
					</div>,
					document.body,
				)}
		</span>
	);
};
